import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import React, { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import Button from 'components/field/Button';
import SwitchInput from 'components/field/SwitchInput';
import FormItem from 'components/form/FormItem';
import * as F from 'components/form/FormLayout.styled';
import { FormFooter } from 'components/form/FormLayout.styled';
import Authorization from 'components/global/Authorization';
import Card from 'components/global/Card';
import FormHeader from 'components/global/FormHeader';
import Typography from 'components/global/Typography';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { NOTIFICATION } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

import * as S from './ProfileSettings.styled';

const defaultValues = {
	newOrder: false,
	deliveredOrder: false,
	orderNeedConfirmation: false,
	orderSLABreach: false,
	agedOrder: false,
	announcement: false,
	returnWaitingConfirmation: false,
	shipmentCreated: false,
	customerReview: false,
	returnRequestReceived: false,
	lowStock: false,
	dropOffShipment: false,
	codPayment: false
};
const childStyle = { width: '5rem' };
const subtitleStyles = {
	margin: '1rem 1rem 2rem 1rem'
};

const NOTIFICATION_SECTIONS = [
	{
		id: 'orderManagement',
		title: 'orderManagementSystem',
		authPath: null,
		fields: [
			{ name: 'newOrder', label: 'noOfNewOrders', description: 'newOrder.description' },
			{ name: 'deliveredOrder', label: 'deliveredOrders', description: 'deliveredOrder.description' },
			{
				name: 'orderNeedConfirmation',
				label: 'orderNeedConfirmation',
				description: 'orderNeedConfirmation.description'
			},
			{ name: 'shipmentCreated', label: 'shipmentCreated', description: 'shipmentCreated.description' },
			{ name: 'dropOffShipment', label: 'dropOffShipment', description: 'dropOffShipment.description' },
			{ name: 'heldForPickupOrder', label: 'heldForPickupOrder', description: 'heldForPickupOrder.description' },
			{
				name: 'codPayment',
				label: 'codPayment',
				description: 'codPayment.description',
				authPath: '/notification-settings/codPayment'
			}
		]
	},
	{
		id: 'returnManagement',
		title: 'returnManagementSystem',
		authPath: 'RMSModule',
		fields: [
			{
				name: 'returnWaitingConfirmation',
				label: 'returnWaitingConfirmation',
				description: 'returnWaitingConfirmation.description'
			},
			{
				name: 'returnRequestReceived',
				label: 'returnRequestReceived',
				description: 'returnRequestReceived.description'
			}
		]
	},
	{
		id: 'lastMileManagement',
		title: 'lastMileManagementSystem',
		authPath: 'SLAModule',
		fields: [
			{
				name: 'orderSLABreach',
				label: 'orderSLABreach',
				description: 'orderSLABreach.description'
			},
			{
				name: 'agedOrder',
				label: 'agedOrder',
				description: 'agedOrder.description'
			}
		]
	},
	{
		id: 'warehouseManagement',
		title: 'warehouseManagementSystem',
		authPath: 'WMSModule',
		fields: [
			{
				name: 'lowStock',
				label: 'lowStock',
				description: 'lowStock.description'
			}
		]
	},
	{
		id: 'general',
		title: 'general',
		authPath: 'WMSModule',
		fields: [
			{
				name: 'announcement',
				label: 'announcement',
				description: 'announcement.description'
			}
		]
	},
	{
		id: 'smsModule',
		title: 'smsModuleNotifications',
		authPath: 'SMSModule',
		fields: [
			{
				name: 'customerReview',
				label: 'customerReviews',
				description: 'customerReview.description'
			}
		]
	}
];
const NotificationSettings = () => {
	const axios = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					newOrder: yup.boolean(),
					deliveredOrder: yup.boolean(),
					orderNeedConfirmation: yup.boolean(),
					orderSLABreach: yup.boolean(),
					agedOrder: yup.boolean(),
					announcement: yup.boolean(),
					returnWaitingConfirmation: yup.boolean(),
					shipmentCreated: yup.boolean(),
					customerReview: yup.boolean(),
					returnRequestReceived: yup.boolean(),
					lowStock: yup.boolean(),
					dropOffShipment: yup.boolean(),
					codPayment: yup.boolean(),
					heldForPickupOrder: yup.boolean()
				})
				.required(),
		[]
	);

	const fillForm = useCallback(async () => {
		try {
			const response = await axios.post(NOTIFICATION.GET_NOTIFICATION_SETTINGS);
			if (response.status === 200) {
				const { data } = response;
				return data;
			} else {
				AppLogger.error(response);
				showError();
				return defaultValues;
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
			return defaultValues;
		}
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading },
		reset
	} = formMethods;

	const onSubmit = async (data) => {
		try {
			const response = await axios.post(NOTIFICATION.UPDATE_NOTIFICATION_SETTINGS, data);
			if (response.status === 200) {
				showSuccess();
				reset(data);
			} else {
				AppLogger.error(response);
				showError({
					header: 'noChangesError',
					message: 'noChangeErrorMessage'
				});
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};
	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const SaveButton = () => (
		<Button
			lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
			className="saveButton"
			onClick={handleSubmit(onSubmit, AppLogger.log)}
			icon={faFloppyDisk}
			disabled={!isDirty}
			disabledTooltip={!isDirty && 'formInfo.noChanges'}
			label="save"
			variant="blue"
		/>
	);

	return (
		<div>
			<HeaderContainerFormHeader className="settingsMaxWidth settingsFormHeader">
				<FormHeader boldTitle="notificationSettings" />
				<S.ButtonContainer>
					<SaveButton onSubmit={onSubmit} />
				</S.ButtonContainer>
			</HeaderContainerFormHeader>
			<S.StyledForm formMethods={formMethods} borderless>
				<S.NotificationSettingsContainer>
					{NOTIFICATION_SECTIONS.map((section) => (
						<NotificationSection key={section.id} section={section} isLoading={isLoading} />
					))}
				</S.NotificationSettingsContainer>
			</S.StyledForm>
			<FormFooter>
				<SaveButton onClick={handleSubmit(onSubmit, AppLogger.log)} />
			</FormFooter>
		</div>
	);
};

const NotificationSection = ({ section, isLoading }) => {
	const content = (
		<Card className="settingsMaxWidth" fullWidth>
			<Typography className="titleMaxWidth" fontWeight="500" style={subtitleStyles}>
				{section.title}
			</Typography>
			{section.fields.map((field, index) => {
				const fieldContent = (
					<>
						{index > 0 && <F.Line className="line" />}
						<FormItem
							isLoading={isLoading}
							direction="row-reverse"
							label={field.label}
							description={field.description}
							childStyle={childStyle}>
							<SwitchInput name={field.name} />
						</FormItem>
					</>
				);

				return field.authPath ? (
					<Authorization key={field.name} path={field.authPath}>
						{fieldContent}
					</Authorization>
				) : (
					<React.Fragment key={field.name}>{fieldContent}</React.Fragment>
				);
			})}
		</Card>
	);

	return section.authPath ? <Authorization path={section.authPath}>{content}</Authorization> : content;
};

export default NotificationSettings;
